import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Card, EmptyState, Skeleton, Stat } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { currencyFlag, formatAmount, formatFullDate, formatPercent, formatRate } from '@/lib/format';

export const Route = createFileRoute('/s/$slug')({ component: SharedView });

/**
 * The public, unauthenticated view of a share link.
 *
 * Positions and aggregates only. Individual rows never cross this boundary,
 * because the note field is where anything personal would be - and this page can
 * be opened by anyone who has the URL.
 */
function SharedView() {
  const { slug } = Route.useParams();
  const { t, i18n } = useTranslation();

  const { data, isLoading, error } = useQuery({
    queryKey: ['share', slug],
    queryFn: () => api.shares.public(slug),
    retry: false,
  });

  return (
    <div className="min-h-dvh bg-[var(--bg)]">
      <header className="mx-auto flex w-full max-w-3xl items-center justify-between px-5 py-5">
        <Link to="/" className="text-[19px] font-semibold tracking-tight">
          {t('common.appNameFull')}
        </Link>
        <span className="flex items-center gap-1.5 rounded-full bg-[var(--fill-3)] px-3 py-1 text-[13px] text-secondary">
          <EyeOff size={13} />
          {t('common.readOnly')}
        </span>
      </header>

      <main className="mx-auto w-full max-w-3xl px-5 pb-16">
        {isLoading ? (
          <>
            <Skeleton className="h-8 w-40" />
            <Skeleton className="mt-4 h-40 w-full" />
          </>
        ) : error || !data ? (
          <EmptyState
            title="链接不存在或已失效"
            body="分享链接可能已被吊销，或者已经过期。"
            action={
              <Link to="/">
                <Button variant="secondary">回到首页</Button>
              </Link>
            }
          />
        ) : (
          <>
            <h1 className="text-[28px] font-bold tracking-tight">{data.displayName}</h1>
            <p className="pb-6 text-[15px] text-secondary">
              持仓与成本概览。单条记录与备注不在分享范围内。
            </p>

            <div className="flex flex-col gap-4">
              {data.items.map((position) => (
                <Card key={`${position.baseCurrency}-${position.quoteCurrency}`}>
                  <div className="flex items-center gap-2 pb-4">
                    <span aria-hidden className="text-[22px]">
                      {currencyFlag(position.quoteCurrency)}
                    </span>
                    <span className="text-[17px] font-semibold">{position.quoteCurrency}</span>
                    <span className="text-[13px] text-secondary">
                      / {position.baseCurrency}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-4">
                    <Stat
                      label={t('position.holding')}
                      value={formatAmount(position.netQuote, position.quoteCurrency)}
                      sub={position.quoteCurrency}
                    />
                    <Stat
                      label={t('position.averageRate')}
                      value={
                        position.averageRate === null ? '—' : formatRate(position.averageRate)
                      }
                    />
                    {position.market && (
                      <Stat
                        label={t('position.marketRate')}
                        value={formatRate(position.market.marketRate)}
                        sub={
                          position.market.diffPct === null
                            ? undefined
                            : formatPercent(position.market.diffPct)
                        }
                        tone={
                          position.market.verdict === 'better'
                            ? 'positive'
                            : position.market.verdict === 'worse'
                              ? 'negative'
                              : 'neutral'
                        }
                      />
                    )}
                    <Stat
                      label={t('position.realized')}
                      value={formatAmount(position.realizedPnl, position.baseCurrency)}
                      tone={
                        Number(position.realizedPnl) > 0
                          ? 'positive'
                          : Number(position.realizedPnl) < 0
                            ? 'negative'
                            : 'neutral'
                      }
                      sub={position.baseCurrency}
                    />
                  </div>

                  {position.marketAsOf && (
                    <p className="tnum pt-4 text-[13px] text-tertiary">
                      {t('position.asOf', {
                        date: formatFullDate(position.marketAsOf, i18n.language),
                      })}
                    </p>
                  )}
                </Card>
              ))}
            </div>

            <div className="pt-8 text-center">
              <Link to="/register">
                <Button variant="secondary">{t('auth.signUp')}</Button>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
